// =============================================================
// intro-index.js — On-load intro index strip.
//
// Shows a temporary 3-second labels strip with a directional
// sweep highlight. Used by both A/B variants:
//   - Swipe   → orientation "horizontal", sweep left → right.
//   - Onepage → orientation "vertical",   sweep bottom → top.
//
// Each label is an anchor link (clickable). After the duration,
// the strip animates out and is removed. The permanent nav
// (passed via `hideEl`) is hidden during the intro and revealed
// after it completes.
// =============================================================

const DEFAULT_LABELS = [
  { id: "section-hero",        label: "Home" },
  { id: "section-experience",  label: "Experience" },
  { id: "section-gallery",     label: "Gallery" },
  { id: "section-perfect-for", label: "Perfect For" },
  { id: "section-about",       label: "About" },
  { id: "section-contact",     label: "Contact" },
];

const isReducedMotion = () =>
  window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

/**
 * Play the intro index animation.
 *
 * @param {Object}  opts
 * @param {"horizontal"|"vertical"} [opts.orientation="horizontal"]
 * @param {number}  [opts.duration=3000]   Total ms (entrance + sweep + exit).
 * @param {Array}   [opts.labels]          Defaults to the 6 site sections.
 * @param {string|Element|null} [opts.hideEl]
 *        Selector or element to hide during the intro
 *        (e.g. `.nav-controls` for swipe, `.anchor-nav` for onepage).
 * @returns {Promise<void>} Resolves when the intro finishes.
 */
export function playIntroIndex({
  orientation = "horizontal",
  duration = 3000,
  labels = DEFAULT_LABELS,
  hideEl = null,
} = {}) {
  return new Promise((resolve) => {
    // Reduced-motion users skip the splash.
    if (isReducedMotion()) {
      resolve();
      return;
    }

    // Hide target nav during intro (revealed in cleanup).
    let target = null;
    if (hideEl) {
      target = typeof hideEl === "string" ? document.querySelector(hideEl) : hideEl;
      if (target) target.classList.add("is-init-hidden");
    }

    // Build DOM.
    const root = document.createElement("div");
    root.className = `intro-index intro-index--${orientation}`;
    root.setAttribute("aria-hidden", "true");

    const row = document.createElement("div");
    row.className = "intro-index-row";

    labels.forEach(({ id, label }) => {
      const a = document.createElement("a");
      a.className = "intro-label";
      a.href = `#${id}`;
      a.textContent = label;
      a.dataset.section = id;
      row.appendChild(a);
    });

    root.appendChild(row);
    document.body.appendChild(root);

    const items = [...row.querySelectorAll(".intro-label")];

    // Timing budget (ms): entrance + sweep + exit = duration.
    const enterMs = Math.min(450, Math.round(duration * 0.15));
    const exitMs  = Math.min(450, Math.round(duration * 0.15));
    const sweepMs = Math.max(duration - enterMs - exitMs, 0);
    const stepMs  = items.length ? sweepMs / items.length : 0;

    const useGsap = !!window.gsap;

    // Entrance direction: horizontal slides from left, vertical from bottom.
    const enterFrom = orientation === "horizontal"
      ? { x: -120, y: 0 }
      : { x: 0,    y: 120 };

    if (useGsap) {
      window.gsap.set(row, { ...enterFrom, opacity: 0 });
      window.gsap.to(row, {
        x: 0,
        y: 0,
        opacity: 1,
        duration: enterMs / 1000,
        ease: "back.out(1.5)",
      });
      // Stagger label reveal in sweep direction during entrance.
      window.gsap.fromTo(
        items,
        { opacity: 0, scale: 0.85 },
        {
          opacity: 1,
          scale: 1,
          duration: 0.32,
          ease: "back.out(1.6)",
          stagger: enterMs / 1000 / Math.max(items.length - 1, 1),
        }
      );
    } else {
      row.style.opacity = "1";
      row.style.transform = "translate(0,0)";
      items.forEach((it) => { it.style.opacity = "1"; });
    }

    // Sweep highlight one label at a time.
    const sweepTimers = [];
    items.forEach((item, i) => {
      sweepTimers.push(
        setTimeout(() => {
          items.forEach((it) => it.classList.remove("is-active"));
          item.classList.add("is-active");
        }, enterMs + i * stepMs)
      );
    });

    // Exit + cleanup.
    const exitTimer = setTimeout(() => {
      const finish = () => {
        root.remove();
        if (target) target.classList.remove("is-init-hidden");
        resolve();
      };
      if (useGsap) {
        window.gsap.to(row, {
          ...enterFrom,
          opacity: 0,
          duration: exitMs / 1000,
          ease: "power2.in",
          onComplete: finish,
        });
      } else {
        finish();
      }
    }, duration - exitMs);

    // Defensive cleanup if the page is hidden mid-animation.
    document.addEventListener(
      "visibilitychange",
      () => {
        if (document.hidden) {
          sweepTimers.forEach(clearTimeout);
          clearTimeout(exitTimer);
          root.remove();
          if (target) target.classList.remove("is-init-hidden");
          resolve();
        }
      },
      { once: true }
    );
  });
}
